/**
 * TipologiaColoriContext — mappa risolta dei colori tipologia (PR-I Fase 5).
 *
 * Fallback trasparente: senza Provider (o prima del fetch) il context
 * restituisce i DEFAULT. Conseguenza: i test esistenti di AgendaEventBlock /
 * stampa / filtri passano SENZA wrapper e vedono i colori PR8a.
 *
 * I renderer invocati da react-big-calendar NON ospitano hook di rete:
 * leggono solo da questo context via `useTipoColors`.
 */

import {
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import type { TipoAppuntamento } from '@shared/types/appuntamento';
import type { TipoColorPair } from '@shared/constants/appuntamento-colors';
import {
  DEFAULT_TIPOLOGIA_COLORI,
  useTipologiaColori,
  type TipologiaColoriMap,
} from '../../../hooks/useTipologiaColori';

const TipologiaColoriContext =
  createContext<TipologiaColoriMap>(DEFAULT_TIPOLOGIA_COLORI);

export function TipologiaColoriProvider({ children }: { children: ReactNode }) {
  const { data } = useTipologiaColori();
  return (
    <TipologiaColoriContext.Provider value={data ?? DEFAULT_TIPOLOGIA_COLORI}>
      {children}
    </TipologiaColoriContext.Provider>
  );
}

/** Mappa completa tipologia → colori (sempre popolata, mai vuota). */
// eslint-disable-next-line react-refresh/only-export-components -- hook del Provider
export function useTipologiaColoriMap(): TipologiaColoriMap {
  return useContext(TipologiaColoriContext);
}

/**
 * Colori di una tipologia dalla STESSA sorgente dell'agenda (D8).
 * Fallback su `altro` per documenti legacy con tipo sconosciuto.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook del Provider
export function useTipoColors(tipo: TipoAppuntamento): TipoColorPair {
  const map = useContext(TipologiaColoriContext);
  return map[tipo] ?? map.altro;
}
